/**
 * Analytics store — computes metrics from pipeline data.
 * NOT persisted (recomputed on demand from pipeline store).
 */

import { create } from 'zustand';
import { usePipelineStore } from './pipeline.store';
import type { PipelineStatus } from '@/types/pipeline';

interface AnalyticsOverview {
  totalJobs: number;
  avgMatchScore: number;
  cvsGenerated: number;
  applicationsSent: number;
}

interface SkillGapEntry {
  skill: string;
  demandCount: number;
}

interface ScoreTrendEntry {
  date: string;
  score: number;
}

type FunnelCounts = Record<PipelineStatus, number>;

interface AnalyticsState {
  overview: AnalyticsOverview;
  skillsGap: SkillGapEntry[];
  scoreTrend: ScoreTrendEntry[];
  funnel: FunnelCounts;
  lastComputed: string;       // ISO date
}

interface AnalyticsActions {
  recompute: () => void;
}

export type AnalyticsStore = AnalyticsState & AnalyticsActions;

const EMPTY_FUNNEL: FunnelCounts = {
  saved: 0,
  applied: 0,
  interview: 0,
  offer: 0,
  rejected: 0,
};

export const useAnalyticsStore = create<AnalyticsStore>()(
  (set) => ({
    // State (defaults — recomputed on demand)
    overview: {
      totalJobs: 0,
      avgMatchScore: 0,
      cvsGenerated: 0,
      applicationsSent: 0,
    },
    skillsGap: [],
    scoreTrend: [],
    funnel: { ...EMPTY_FUNNEL },
    lastComputed: '',

    // Actions

    /**
     * Recompute all analytics from the current pipeline store state.
     * Call this whenever the UI needs fresh metrics.
     */
    recompute: () => {
      const { jobs } = usePipelineStore.getState();

      // --- Overview ---
      const totalJobs = jobs.length;

      const avgMatchScore =
        totalJobs > 0
          ? Math.round(
              jobs.reduce((sum, job) => sum + job.matchScore, 0) / totalJobs
            )
          : 0;

      const cvsGenerated = jobs.filter((job) => job.generatedCVId).length;

      const applicationsSent = jobs.filter(
        (job) =>
          job.status === 'applied' ||
          job.status === 'interview' ||
          job.status === 'offer'
      ).length;

      // --- Funnel ---
      const funnel: FunnelCounts = { ...EMPTY_FUNNEL };
      for (const job of jobs) {
        funnel[job.status] += 1;
      }

      // --- Score Trend ---
      // Group jobs by addedAt date and compute average score per day
      const scoreByDate = new Map<string, { total: number; count: number }>();
      for (const job of jobs) {
        const dateKey = job.addedAt.slice(0, 10); // YYYY-MM-DD
        const existing = scoreByDate.get(dateKey);
        if (existing) {
          existing.total += job.matchScore;
          existing.count += 1;
        } else {
          scoreByDate.set(dateKey, { total: job.matchScore, count: 1 });
        }
      }

      const scoreTrend: ScoreTrendEntry[] = Array.from(scoreByDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, { total, count }]) => ({
          date,
          score: Math.round(total / count),
        }));

      // --- Skills Gap ---
      // (Placeholder: skills gap requires vacancy requirements data.
      //  For now, expose empty array — will be populated when vacancy
      //  skills tracking is integrated.)
      const skillsGap: SkillGapEntry[] = [];

      set({
        overview: {
          totalJobs,
          avgMatchScore,
          cvsGenerated,
          applicationsSent,
        },
        skillsGap,
        scoreTrend,
        funnel,
        lastComputed: new Date().toISOString(),
      });
    },
  })
);
